import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { AlertCircle, Clock, Mail, XCircle } from 'lucide-react';
import { useInvitationPreview, useAcceptInvitation } from '../../hooks/useOrganizations';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrentOrganization } from '../../contexts/OrganizationContext';
import { Badge, Button } from '../../ui';
import './AcceptInvitationPage.css';

const LINK_PRIMARY = 'ui-button ui-button--primary ui-button--md';
const LINK_SECONDARY = 'ui-button ui-button--secondary ui-button--md';

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { setCurrentOrgId } = useCurrentOrganization();

  const {
    data: preview,
    isLoading: previewLoading,
    error: previewError,
  } = useInvitationPreview(token ?? undefined);

  const acceptInvitation = useAcceptInvitation();

  const handleAccept = async () => {
    if (!token) return;

    try {
      const result = await acceptInvitation.mutateAsync(token);
      setCurrentOrgId(result.organization_id);
      navigate(`/organizations/${result.organization_id}`);
    } catch {
      /* handled by mutation */
    }
  };

  if (authLoading || previewLoading) {
    return (
      <div className="accept-invite-page">
        <div className="invite-card">
          <div className="invite-card__loading">
            <div className="spinner" />
            <p>Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="accept-invite-page">
        <div className="invite-card invite-card--error">
          <div className="invite-card__icon invite-card__icon--error">
            <AlertCircle size={24} aria-hidden="true" />
          </div>
          <h1>Invalid Invitation</h1>
          <p>No invitation token was provided.</p>
          <Link to="/" className={LINK_PRIMARY}>
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (previewError) {
    return (
      <div className="accept-invite-page">
        <div className="invite-card invite-card--error">
          <div className="invite-card__icon invite-card__icon--error">
            <XCircle size={24} aria-hidden="true" />
          </div>
          <h1>Invitation Not Found</h1>
          <p>This invitation may have been revoked or does not exist.</p>
          <Link to="/" className={LINK_PRIMARY}>
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (preview?.expired) {
    return (
      <div className="accept-invite-page">
        <div className="invite-card invite-card--error">
          <div className="invite-card__icon invite-card__icon--error">
            <Clock size={24} aria-hidden="true" />
          </div>
          <h1>Invitation Expired</h1>
          <p>
            This invitation has expired. Please contact the organization administrator for a new
            invitation.
          </p>
          <Link to="/" className={LINK_PRIMARY}>
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const redirectUrl = encodeURIComponent(`/invite/accept?token=${token}`);
    return (
      <div className="accept-invite-page">
        <div className="invite-card">
          <div className="invite-card__icon">
            <Mail size={24} aria-hidden="true" />
          </div>
          <h1>You're Invited!</h1>
          <div className="invite-preview">
            <p className="invite-org">{preview?.organization_name}</p>
            <p className="invite-details">
              <strong>{preview?.inviter_name}</strong> has invited you to join as{' '}
              <Badge tone="info">{preview?.role}</Badge>
            </p>
          </div>
          <div className="invite-actions">
            <p className="invite-login-prompt">
              Sign in or create an account to accept this invitation.
            </p>
            <Link to={`/login?redirect=${redirectUrl}`} className={LINK_PRIMARY}>
              Sign In
            </Link>
            <Link to={`/register?redirect=${redirectUrl}`} className={LINK_SECONDARY}>
              Create Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invite-page">
      <div className="invite-card">
        <div className="invite-card__icon">
          <Mail size={24} aria-hidden="true" />
        </div>
        <h1>You're Invited!</h1>
        <div className="invite-preview">
          <p className="invite-org">{preview?.organization_name}</p>
          <p className="invite-details">
            <strong>{preview?.inviter_name}</strong> has invited you to join as{' '}
            <Badge tone="info">{preview?.role}</Badge>
          </p>
        </div>

        {acceptInvitation.isError && (
          <p className="invite-error">
            {acceptInvitation.error?.message || 'Failed to accept invitation'}
          </p>
        )}

        <div className="invite-actions">
          <Button size="lg" block onClick={handleAccept} loading={acceptInvitation.isPending}>
            {acceptInvitation.isPending ? 'Joining...' : 'Accept & Join Organization'}
          </Button>
          <Link to="/" className={LINK_SECONDARY}>
            Decline
          </Link>
        </div>
      </div>
    </div>
  );
}
